import React, { useEffect } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import Seo from "../../components/Seo";
import { Avatar, PageHeader, Spinner } from "../../components/lawyer/LawyerUi";
import { isBlankFile } from "../../services/lawyerApi";

const names = (list) => (Array.isArray(list) ? list.map((i) => i?.name || i).filter(Boolean) : []);

const languageNames = (value) => {
  if (!value) return [];
  const list = Array.isArray(value) ? value : String(value).split(",");
  return list.map((l) => (typeof l === "string" ? l.trim() : l?.name)).filter(Boolean);
};

const isPdf = (url) => /\.pdf($|\?)/i.test(url || "");

const shareUrl = (name) => {
  const apiRoot = (process.env.REACT_APP_API_URL || "").replace(/\/api\/?$/, "");
  return `${apiRoot}/share/${encodeURIComponent((name || "").toLowerCase().replace(/ /g, "_"))}`;
};

const Stars = ({ value }) => (
  <span className="lwp-stars" aria-label={`${value} out of 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <i key={n} className={`bi ${value >= n ? "bi-star-fill" : value >= n - 0.5 ? "bi-star-half" : "bi-star"}`} />
    ))}
  </span>
);

const DocCard = ({ url, label }) => {
  if (isBlankFile(url)) {
    return (
      <div className="lwp-doc-card empty">
        <i className="bi bi-file-earmark-x" />
        <span>{label}</span>
        <small>Not uploaded</small>
      </div>
    );
  }
  return (
    <a className="lwp-doc-card" href={url} target="_blank" rel="noreferrer">
      {isPdf(url) ? <i className="bi bi-file-earmark-pdf-fill" /> : <img src={url} alt={label} loading="lazy" />}
      <span>{label}</span>
    </a>
  );
};

const LawyerProfile = () => {
  const { profile, refreshProfile } = useOutletContext();

  // The layout loads the profile once; refresh here so edits made elsewhere show up.
  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  if (!profile?.email) {
    return (
      <div className="lwp-page">
        <PageHeader title="My Profile" />
        <Spinner />
      </div>
    );
  }

  const verified = Number(profile.is_approved) === 1;
  const totalReviews = Number(profile.total_reviews) || 0;
  const rating = Number(profile.rating) || 0;
  const roles = names(profile.arbitrators);
  const specialities = [...names(profile.categories), ...names(profile.sub_categories)];
  const jurisdictions = names(profile.jurisdictions);
  const languages = languageNames(profile.languages);
  const attachments = [...(profile.attachments || []), ...(profile.images || [])].filter((u) => !isBlankFile(u));
  const reviews = profile.reviews || [];

  const personal = [
    ["City", profile.city],
    ["Country", profile.country],
    ["Nationality", profile.nationality],
    ["Phone Number", profile.phone],
    ["Language", languages.join(", ")],
    ["Email", profile.email],
  ].filter(([, v]) => v);

  const share = async () => {
    const url = shareUrl(profile.name);
    try {
      if (navigator.share) {
        await navigator.share({ title: profile.name, text: `${profile.name} on Legal Platform`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Profile link copied");
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Couldn't share the profile link.");
    }
  };

  return (
    <div className="lwp-page lwp-profile">
      <Seo title="My Profile | Legal Platform" noindex />
      <PageHeader
        title="My Profile"
        actions={
          <>
            <Link to="/lawyer/profile/edit" className="lwp-head-btn filled" aria-label="Edit profile" title="Edit profile">
              <i className="bi bi-pencil-fill" />
            </Link>
            <button type="button" className="lwp-head-btn" onClick={share} aria-label="Share profile" title="Share profile">
              <i className="bi bi-box-arrow-up" />
            </button>
          </>
        }
      />

      <div className="lwp-profile-grid">
        <div className="lwp-profile-main">
          <section className="lwp-card lwp-profile-card">
            <div className="lwp-profile-top">
              <Avatar src={profile.picture} alt={profile.name} size={96} />
              <div className="lwp-profile-id">
                <h2>{profile.name}</h2>
                <span>{profile.email}</span>
                <span className="lwp-profile-rating">
                  {totalReviews > 0 ? (
                    <>
                      <Stars value={rating} /> {rating.toFixed(1)} ({totalReviews})
                    </>
                  ) : (
                    <>
                      No reviews (yet) <i className="bi bi-star-fill" />
                    </>
                  )}
                </span>
              </div>
              {verified && <span className="lwp-verified">Verified</span>}
            </div>
            {profile.about && <p className="lwp-profile-about">{profile.about}</p>}
            {profile.profile_update_requested && (
              <p className="lwp-profile-note">
                <i className="bi bi-hourglass-split" /> Profile update pending admin approval
              </p>
            )}
          </section>

          <Link to="/lawyer/business-services" className="lwp-outline-cta">
            Update Business Services
          </Link>

          <h3 className="lwp-section-title">Personal Information</h3>
          <section className="lwp-card">
            <dl className="lwp-info-list">
              {personal.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>

        <div className="lwp-profile-side">
          <h3 className="lwp-section-title">Professional Info</h3>
          {roles.length > 0 && (
            <section className="lwp-card">
              <p className="lwp-info-label">Legal Consultant</p>
              <ul className="lwp-plain-list">
                {roles.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </section>
          )}
          <section className="lwp-card">
            <p className="lwp-info-label">Speciality</p>
            {specialities.length ? (
              <ul className="lwp-plain-list lwp-two-col">
                {specialities.map((s, i) => (
                  <li key={`${s}-${i}`}>{s}</li>
                ))}
              </ul>
            ) : (
              <p className="lwp-muted">Not set</p>
            )}
          </section>

          <h3 className="lwp-section-title">Jurisdiction</h3>
          <section className="lwp-card">
            <p className="lwp-info-label">Jurisdiction</p>
            {jurisdictions.length ? (
              <ul className="lwp-plain-list">
                {jurisdictions.map((j) => (
                  <li key={j}>{j}</li>
                ))}
              </ul>
            ) : (
              <p className="lwp-muted">Not set</p>
            )}
          </section>

          <h3 className="lwp-section-title">Law Degree Certificate</h3>
          <div className="lwp-doc-grid">
            <DocCard url={profile.certificate} label="Law Certificate" />
            <DocCard url={profile.license} label="Legal License" />
          </div>
        </div>
      </div>

      <h3 className="lwp-section-title">Reviews</h3>
      <section className="lwp-card">
        {reviews.length === 0 ? (
          <p className="lwp-no-reviews">
            <i className="bi bi-star-fill" /> No reviews (yet)
          </p>
        ) : (
          <ul className="lwp-review-list">
            {reviews.map((r) => (
              <li key={r.id}>
                <Avatar src={r.user?.picture} alt={r.user?.name} size={44} />
                <div>
                  <div className="lwp-review-head">
                    <strong>{r.user?.name || "Client"}</strong>
                    <time>{moment(r.created_at).format("D MMM YYYY")}</time>
                  </div>
                  <Stars value={Number(r.stars) || 0} />
                  {r.comment && <p>{r.comment}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {attachments.length > 0 && (
        <>
          <h3 className="lwp-section-title">Attachments</h3>
          <div className="lwp-doc-grid lwp-doc-grid-wide">
            {attachments.map((url, i) => (
              <DocCard key={`${url}-${i}`} url={url} label="Attachment" />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LawyerProfile;
